import {
  createPullRequestLifecycleSession,
  type PullRequestLifecycleSession,
} from "./pullRequestLifecycle";
import { createPullRequestReviewSession, type PullRequestReviewSession } from "./pullRequestReview";
import {
  createPullRequestDiscussionSession,
  type PullRequestDiscussionSession,
} from "./pullRequestDiscussion";
import { createPullRequestEditSession, type PullRequestEditSession } from "./pullRequestEdit";
import type { EnvironmentId, ProjectId } from "@afar-research/contracts";
import { create } from "zustand";
import {
  createPullRequestHandoffSession,
  type PullRequestHandoffSession,
} from "./pullRequestHandoff";

import {
  dispatchWorkspaceSurfaceState,
  type WorkspaceSurfaceDescriptor,
  type WorkspaceSurfaceState,
} from "./workspaceSurfaceHost";

export interface PullRequestSurfaceDescriptor extends WorkspaceSurfaceDescriptor<"pull-request"> {
  readonly id: `pull-request:${string}`;
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  /**
   * The GitHub host the pull request lives on, where it is not the Project's own. The Project
   * supplies the checkout and credentials; the host routes the read to one that can serve it.
   */
  readonly host?: string;
  readonly repository: string;
  readonly number: number;
  readonly url: string;
}

export type PullRequestSurfaceTarget = Omit<PullRequestSurfaceDescriptor, "id" | "kind">;

export function pullRequestSurfaceId(
  target: PullRequestSurfaceTarget,
): PullRequestSurfaceDescriptor["id"] {
  // The host is part of the id: the same repository name on two hosts is two pull requests, and
  // one tab must never change its mind about which it is showing.
  const host = target.host === undefined ? "" : `${encodeURIComponent(target.host.toLowerCase())}:`;
  return `pull-request:${encodeURIComponent(target.environmentId)}:${encodeURIComponent(target.projectId)}:${host}${encodeURIComponent(target.repository)}:${target.number}`;
}

export function pullRequestSurface(target: PullRequestSurfaceTarget): PullRequestSurfaceDescriptor {
  return {
    ...target,
    id: pullRequestSurfaceId(target),
    kind: "pull-request",
  };
}

export function decodePullRequestSurface(value: unknown): PullRequestSurfaceDescriptor | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Partial<PullRequestSurfaceDescriptor>;
  if (
    candidate.kind !== "pull-request" ||
    typeof candidate.id !== "string" ||
    typeof candidate.environmentId !== "string" ||
    typeof candidate.projectId !== "string" ||
    typeof candidate.repository !== "string" ||
    candidate.repository.trim().length === 0 ||
    typeof candidate.number !== "number" ||
    !Number.isSafeInteger(candidate.number) ||
    candidate.number < 1 ||
    typeof candidate.url !== "string"
  ) {
    return null;
  }
  const descriptor = candidate as PullRequestSurfaceDescriptor;
  try {
    const url = new URL(descriptor.url);
    const validProtocol = url.protocol === "https:" || url.protocol === "http:";
    const validPath = url.pathname === `/${descriptor.repository}/pull/${descriptor.number}`;
    return descriptor.id === pullRequestSurfaceId(descriptor) && validProtocol && validPath
      ? descriptor
      : null;
  } catch {
    return null;
  }
}

const EMPTY_PANEL: WorkspaceSurfaceState<PullRequestSurfaceDescriptor> = {
  isOpen: false,
  activeSurfaceId: null,
  surfaces: [],
};

interface PullRequestSurfaceStore {
  readonly lifecycles: ReadonlyMap<string, PullRequestLifecycleSession>;
  readonly reviews: ReadonlyMap<string, PullRequestReviewSession>;
  readonly discussions: ReadonlyMap<string, PullRequestDiscussionSession>;
  readonly edits: ReadonlyMap<string, PullRequestEditSession>;
  readonly handoffs: ReadonlyMap<string, PullRequestHandoffSession>;
  readonly panel: WorkspaceSurfaceState<PullRequestSurfaceDescriptor>;
  readonly open: (descriptor: PullRequestSurfaceDescriptor) => void;
  /**
   * Give a pull request its mutation sessions without placing it in the workspace panel, for a
   * detail opened beside a thread. Presentation is the caller's; the sessions are shared, and
   * outlive the component that rendered them exactly as the workspace's do.
   */
  readonly ensureSessions: (descriptor: PullRequestSurfaceDescriptor) => void;
  readonly activate: (surfaceId: string) => void;
  readonly hide: () => void;
  readonly show: () => void;
  readonly close: (surfaceId: string) => void;
  /** Close every pull request but this one. */
  readonly closeOthers: (surfaceId: string) => void;
  /** Close the pull requests after this one, leaving it and everything before it. */
  readonly closeToRight: (surfaceId: string) => void;
  readonly closeAll: () => void;
  readonly reset: () => void;
}

/**
 * Drop the mutation sessions of every pull request that is no longer open. A closed tab takes
 * its unsent review, comment and edit with it, and one rule says so for every way of closing.
 */
function prunedSessions(
  state: PullRequestSurfaceStore,
  panel: WorkspaceSurfaceState<PullRequestSurfaceDescriptor>,
): Pick<PullRequestSurfaceStore, "lifecycles" | "reviews" | "discussions" | "edits" | "handoffs"> {
  const open = new Set<string>(panel.surfaces.map((surface) => surface.id));
  const keep = <T>(sessions: ReadonlyMap<string, T>): ReadonlyMap<string, T> =>
    new Map([...sessions].filter(([id]) => open.has(id)));
  return {
    lifecycles: keep(state.lifecycles),
    reviews: keep(state.reviews),
    discussions: keep(state.discussions),
    edits: keep(state.edits),
    handoffs: keep(state.handoffs),
  };
}

/** The mutation sessions a pull request needs, created once and left alone after that. */
function withSessions(
  state: PullRequestSurfaceStore,
  descriptor: PullRequestSurfaceDescriptor,
): Pick<PullRequestSurfaceStore, "lifecycles" | "reviews" | "discussions" | "edits" | "handoffs"> {
  return {
    lifecycles: state.lifecycles.has(descriptor.id)
      ? state.lifecycles
      : new Map(state.lifecycles).set(descriptor.id, createPullRequestLifecycleSession()),
    reviews: state.reviews.has(descriptor.id)
      ? state.reviews
      : new Map(state.reviews).set(descriptor.id, createPullRequestReviewSession()),
    discussions: state.discussions.has(descriptor.id)
      ? state.discussions
      : new Map(state.discussions).set(descriptor.id, createPullRequestDiscussionSession()),
    edits: state.edits.has(descriptor.id)
      ? state.edits
      : new Map(state.edits).set(descriptor.id, createPullRequestEditSession()),
    handoffs: state.handoffs.has(descriptor.id)
      ? state.handoffs
      : new Map(state.handoffs).set(descriptor.id, createPullRequestHandoffSession()),
  };
}

export const usePullRequestSurfaceStore = create<PullRequestSurfaceStore>((set) => ({
  panel: EMPTY_PANEL,
  handoffs: new Map(),
  edits: new Map(),
  discussions: new Map(),
  reviews: new Map(),
  lifecycles: new Map(),
  ensureSessions: (descriptor) => set((state) => withSessions(state, descriptor)),
  open: (descriptor) =>
    set((state) => ({
      ...withSessions(state, descriptor),
      panel: dispatchWorkspaceSurfaceState(state.panel, { type: "open", descriptor }),
    })),
  activate: (surfaceId) =>
    set((state) => ({
      panel: dispatchWorkspaceSurfaceState(state.panel, { type: "activate", surfaceId }),
    })),
  hide: () =>
    set((state) => ({ panel: dispatchWorkspaceSurfaceState(state.panel, { type: "hide" }) })),
  show: () =>
    set((state) => ({ panel: dispatchWorkspaceSurfaceState(state.panel, { type: "show" }) })),
  close: (surfaceId) =>
    set((state) => {
      const panel = dispatchWorkspaceSurfaceState(state.panel, { type: "close", surfaceId });
      return { panel, ...prunedSessions(state, panel) };
    }),
  closeOthers: (surfaceId) =>
    set((state) => {
      const panel = state.panel.surfaces
        .filter((surface) => surface.id !== surfaceId)
        .reduce(
          (current, surface) =>
            dispatchWorkspaceSurfaceState(current, { type: "close", surfaceId: surface.id }),
          state.panel,
        );
      return { panel, ...prunedSessions(state, panel) };
    }),
  closeToRight: (surfaceId) =>
    set((state) => {
      const index = state.panel.surfaces.findIndex((surface) => surface.id === surfaceId);
      if (index < 0) return {};
      const panel = state.panel.surfaces
        .slice(index + 1)
        .reduce(
          (current, surface) =>
            dispatchWorkspaceSurfaceState(current, { type: "close", surfaceId: surface.id }),
          state.panel,
        );
      return { panel, ...prunedSessions(state, panel) };
    }),
  closeAll: () =>
    set((state) => ({
      panel: dispatchWorkspaceSurfaceState(state.panel, { type: "close-all" }),
      handoffs: new Map(),
      edits: new Map(),
      discussions: new Map(),
      reviews: new Map(),
      lifecycles: new Map(),
    })),
  reset: () =>
    set({
      panel: EMPTY_PANEL,
      handoffs: new Map(),
      edits: new Map(),
      discussions: new Map(),
      reviews: new Map(),
      lifecycles: new Map(),
    }),
}));
